import React from 'react'

import { RegistrationStatusLabel } from './RegistrationStatusLabel'
import { RegistrationListItemActionsMenu } from './RegistrationListItemActionsMenu'

import type { TenantRegistration } from '../../../../types/registration'
import type { TenantRegistrationActions } from '../../../../types/registration/actions'
import { WARNING_ONLY_FIELDS } from '../constants'

export interface RegistrationListItemProps {
  registration: TenantRegistration
  isOpen: boolean
  areActionsHidden: boolean
  isChecked?: boolean
  onCheckedChange?: () => void
  onClick: () => void
  onActionClick: (action: TenantRegistrationActions) => void
}

export const RegistrationListItem: React.FC<RegistrationListItemProps> = (props) => {
  const isDuplicate = props.registration.isDuplicate
  const missingFields = props.registration.missingFields ?? []
  const errorFields = missingFields.filter((f) => !WARNING_ONLY_FIELDS.has(f))
  const missingWarnings = missingFields.filter((f) => WARNING_ONLY_FIELDS.has(f))
  const formatWarnings = props.registration.warningFields ?? []
  const hasValidationError = errorFields.length > 0
  const hasWarning = missingWarnings.length > 0 || formatWarnings.length > 0
  const showCheckbox = props.isChecked !== undefined

  const rowBg = hasValidationError
    ? 'bg-red-50 dark:bg-red-950/20'
    : isDuplicate || hasWarning
      ? 'bg-amber-50 dark:bg-amber-950/30'
      : 'bg-white dark:bg-slate-800'

  return (
    <>
      <tr onClick={props.onClick} className={`${rowBg} cursor-pointer`}>
        {showCheckbox && (
          <td className="w-10 px-3 py-4" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-indigo-600 focus:ring-indigo-600 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              checked={props.isChecked}
              disabled={hasValidationError}
              onChange={props.onCheckedChange}
            />
          </td>
        )}
        <td className="px-6 py-4">
          <div className="text-sm font-medium text-slate-900 dark:text-slate-100">{props.registration.tenant.name}</div>
          {hasValidationError && (
            <div className="mt-1">
              <span className="inline-flex items-center rounded-md bg-red-100 dark:bg-red-900/50 px-2 py-0.5 text-xs font-medium text-red-800 dark:text-red-300">
                Missing: {errorFields.join(', ')}
              </span>
            </div>
          )}
          {missingWarnings.length > 0 && (
            <div className={hasValidationError ? 'mt-0.5' : 'mt-1'}>
              <span className="inline-flex items-center rounded-md bg-amber-100 dark:bg-amber-900/50 px-2 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-300">
                Missing: {missingWarnings.join(', ')}
              </span>
            </div>
          )}
          {formatWarnings.map((warning, i) => (
            <div key={i} className={hasValidationError || missingWarnings.length > 0 || i > 0 ? 'mt-0.5' : 'mt-1'}>
              <span className="inline-flex items-center rounded-md bg-amber-100 dark:bg-amber-900/50 px-2 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-300">
                {warning}
              </span>
            </div>
          ))}
          {isDuplicate && (
            <div className={hasValidationError ? 'mt-0.5' : 'mt-1'}>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-flex items-center rounded-md bg-amber-100 dark:bg-amber-900/50 px-2 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-300">
                  Duplicate
                </span>
                {props.registration.existingPropertyName && (
                  <span className="text-xs text-amber-700 dark:text-amber-400">Already imported in {props.registration.existingPropertyName}</span>
                )}
              </span>
            </div>
          )}
        </td>
        <td className="px-6 py-4" colSpan={props.areActionsHidden ? 3 : 1}>
          <div className="text-sm text-slate-900 dark:text-slate-100">{`${props.registration.tenant.contact.firstName} ${props.registration.tenant.contact.lastName}`}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">{props.registration.tenant.contact.emailAddress}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">{props.registration.tenant.contact.phoneNumber}</div>
        </td>
        {!props.areActionsHidden && (
          <>
            <td className="px-6 py-4 whitespace-nowrap">
              <RegistrationStatusLabel status={props.registration.status} />
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <RegistrationListItemActionsMenu status={props.registration.status} onActionClick={props.onActionClick} />
            </td>
          </>
        )}
      </tr>
      {props.isOpen && (
        <tr className="bg-white dark:bg-slate-800">
          <td colSpan={showCheckbox ? 5 : 4} className="px-6 py-3">
            {props.registration.tenant.accounts.map((account, accountIdx) => (
              <div key={accountIdx} className={accountIdx > 0 ? 'mt-3 pt-3 border-t border-slate-100 dark:border-slate-700' : ''}>
                <div className="pl-4 flex gap-x-8">
                  <div className="min-w-0">
                    <div className="text-xs font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500">Utility Account</div>
                    <div className="text-sm text-slate-900 dark:text-slate-100">{account.name}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 font-mono">{account.number}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500">Billing Address</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">{account.billingAddress.address}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">{`${account.billingAddress.city}, ${account.billingAddress.state}, ${account.billingAddress.zipCode}`}</div>
                  </div>
                </div>
                {account.agreements.map((agreement, agreementIdx) => (
                  <div key={agreementIdx} className="pl-10 mt-2 flex gap-x-8">
                    <div className="min-w-0">
                      <div className="text-xs font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500">Service Agreement</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400 font-mono">{agreement.number}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {agreement.meterNumber} &middot; {agreement.tariffSchedule}
                      </div>
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-medium uppercase tracking-wider text-slate-400 dark:text-slate-500">Service Address</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">{agreement.serviceAddress.address}</div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">{`${agreement.serviceAddress.city}, ${agreement.serviceAddress.state}, ${agreement.serviceAddress.zipCode}`}</div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </td>
        </tr>
      )}
    </>
  )
}

export default RegistrationListItem
